import React, { useState, useEffect } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { AiOutlineDelete } from "react-icons/ai";

const GET_SERVICES = gql`
  query {
    getAdminService {
      services {
        id
        name
      }
    }
  }
`;

const GET_INVENTORY_LIST = gql`
  query {
    getInventoryCategory {
      status
      message
      categories {
        id
        name
      }
    }
  }
`;

const ADD_SERVICE_COMBO = gql`
  mutation addServiceCombo($data: ServiceComboInput!) {
    addServiceCombo(data: $data) {
      status
      message
    }
  }
`;

const UPDATE_SERVICE_COMBO = gql`
  mutation updateServiceCombo($data: UpdateServiceComboInput!) {
    updateServiceCombo(data: $data) {
      status
      message
    }
  }
`;

// Used for both create (comboFormData null/undefined) and edit
// (comboFormData the combo being edited) - mirrors ServiceEditForm's
// inventoryList rows editor and the packageChildServiceIds repeated-select
// pattern, since a combo needs exactly the same two kinds of row: a
// multi-service picker (>=2 services) and a shared inventory list.
const ServiceComboForm = ({ comboFormData, onClose }) => {
  const isEdit = !!(comboFormData && comboFormData.id);

  const { data: servicesData } = useQuery(GET_SERVICES);
  const { data: inventoryData } = useQuery(GET_INVENTORY_LIST);
  const [serviceList, setServiceList] = useState([]);
  const [dataList, setDataList] = useState([]);

  const [addServiceCombo] = useMutation(ADD_SERVICE_COMBO);
  const [updateServiceCombo] = useMutation(UPDATE_SERVICE_COMBO);

  const [name, setName] = useState(comboFormData?.name || "");
  const [priority, setPriority] = useState(comboFormData?.priority ?? 0);
  const [isActive, setIsActive] = useState(
    comboFormData?.isActive !== undefined ? comboFormData.isActive : true,
  );
  const [serviceIds, setServiceIds] = useState(
    comboFormData?.services && comboFormData.services.length > 0
      ? comboFormData.services.map((s) => s.id)
      : ["", ""],
  );
  const [inventoryList, setInventoryList] = useState(
    comboFormData?.inventoryList && comboFormData.inventoryList.length > 0
      ? comboFormData.inventoryList.map((item) => ({
          inventoryId: item.comboInventoryCategory?.id || "",
          price: item.price,
          alternateGroupId: item.alternateGroupId || "",
        }))
      : [{ inventoryId: "", price: "", alternateGroupId: "" }],
  );
  const [error, setError] = useState("");

  useEffect(() => {
    if (servicesData?.getAdminService?.services) {
      setServiceList(servicesData.getAdminService.services);
    }
  }, [servicesData]);

  useEffect(() => {
    if (inventoryData?.getInventoryCategory?.categories) {
      setDataList(inventoryData.getInventoryCategory.categories);
    }
  }, [inventoryData]);

  const handleServiceChange = (index, event) => {
    const { value } = event.target;
    const updated = [...serviceIds];
    updated[index] = value;
    setServiceIds(updated);
  };

  const handleAddService = () => setServiceIds([...serviceIds, ""]);

  const handleRemoveService = (index) => {
    const updated = [...serviceIds];
    updated.splice(index, 1);
    setServiceIds(updated);
  };

  const handleInventoryChange = (index, event) => {
    const { name: field, value } = event.target;
    const updated = [...inventoryList];
    updated[index][field] = value;
    setInventoryList(updated);
  };

  const handleInventoryPriceChange = (index, event) => {
    const { name: field, value } = event.target;
    const updated = [...inventoryList];
    updated[index][field] = parseInt(value, 10);
    setInventoryList(updated);
  };

  const handleAddRow = () => {
    setInventoryList([...inventoryList, { inventoryId: "", price: "", alternateGroupId: "" }]);
  };

  const handleRemoveRow = (index) => {
    const updated = [...inventoryList];
    updated.splice(index, 1);
    setInventoryList(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanServiceIds = [...new Set(serviceIds.filter((id) => id))];
    if (cleanServiceIds.length < 2) {
      setError("Select at least 2 different services for this combo.");
      return;
    }
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError("");

    const cleanInventoryList = inventoryList
      .filter((item) => item.inventoryId)
      .map((item) => ({
        inventoryId: item.inventoryId,
        price: item.price,
        alternateGroupId: item.alternateGroupId || null,
      }));

    const basePayload = {
      name,
      priority: Number(priority) || 0,
      isActive,
      serviceIds: cleanServiceIds,
      inventoryList: cleanInventoryList,
    };

    const action = isEdit
      ? updateServiceCombo({ variables: { data: { id: comboFormData.id, ...basePayload } } })
      : addServiceCombo({ variables: { data: basePayload } });

    action
      .then((res) => {
        const result = isEdit ? res.data.updateServiceCombo : res.data.addServiceCombo;
        if (result.status === "SUCCESS") {
          window.location.reload();
        } else {
          setError(result.message || "Something went wrong");
        }
      })
      .catch((err) => {
        console.log(err);
        setError("Something went wrong");
      });
  };

  return (
    <div className="flex flex-col space-y-6 pt-8 pl-5 pr-5">
      <p className="text-[#FBB033] font-be-jost-pro text-xl font-bold">
        {isEdit ? "Edit Service Combo" : "Add Service Combo"}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col space-y-6">
        <div className="flex space-x-2 lg:space-x-8">
          <input
            type="text"
            placeholder="Combo name*"
            className="px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none text-white w-1/2 lg:w-[25%]"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            type="number"
            placeholder="Priority (tiebreak only)"
            title="Only used to break a tie when two matching combos cover the same number of services - the higher priority wins."
            className="px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none text-white w-1/3"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
          <label className="flex items-center space-x-2 text-white">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>Active</span>
          </label>
        </div>

        <div className="flex flex-col space-y-4">
          <p className="text-white text-sm">
            Services in this combo (matches whenever ALL of these are booked together on the same order):
          </p>
          {serviceIds.map((serviceId, index) => (
            <div className="flex space-x-2 lg:space-x-8" key={index}>
              <select
                className={`px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none ${
                  serviceId === "" ? "text-[#9A9A9A]" : "text-white"
                } w-1/2 lg:w-[25%]`}
                value={serviceId}
                onChange={(e) => handleServiceChange(index, e)}
              >
                <option value="" disabled>
                  Select Service
                </option>
                {serviceList.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <div className="flex items-center">
                <span
                  className="text-[#FBB033] font-be-jost-pro text-base font-normal cursor-pointer"
                  onClick={handleAddService}
                >
                  Add Service +
                </span>
                {serviceIds.length > 2 && (
                  <AiOutlineDelete
                    className="text-[#FBB033] text-2xl cursor-pointer ml-4"
                    onClick={() => handleRemoveService(index)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col space-y-4">
          <p className="text-white text-sm">
            Shared inventory for this combo (overrides each service's own recipe only for the categories listed here):
          </p>
          {inventoryList.map((inventory, index) => (
            <div className="flex space-x-2 lg:space-x-8" key={index}>
              <select
                className={`px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none ${
                  inventory.inventoryId === "" ? "text-[#9A9A9A]" : "text-white"
                } w-1/2 lg:w-[25%]`}
                value={inventory.inventoryId}
                onChange={(e) => handleInventoryChange(index, e)}
                name="inventoryId"
              >
                <option value="" disabled hidden>
                  Inventory*
                </option>
                {dataList.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <div className="flex items-center">
                <span
                  className="text-[#FBB033] font-be-jost-pro text-base font-normal cursor-pointer"
                  onClick={handleAddRow}
                >
                  {`Add Inventory +  `}
                </span>
                {inventoryList.length !== 1 && (
                  <AiOutlineDelete
                    className="text-[#FBB033] text-2xl cursor-pointer"
                    onClick={() => handleRemoveRow(index)}
                  />
                )}
              </div>
              <input
                type="number"
                placeholder="Amount*"
                className="px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none text-white w-1/3"
                value={Number.isNaN(parseInt(inventory.price, 10)) ? "" : parseInt(inventory.price, 10)}
                onChange={(event) => handleInventoryPriceChange(index, event)}
                name="price"
              />
              <input
                type="text"
                placeholder="Alternate group (optional)"
                className="px-2 py-1 bg-[#0c0b0b] border-b-[1px] border-b-[#E0E0E0] outline-none text-white w-1/3"
                value={inventory.alternateGroupId || ""}
                onChange={(event) => handleInventoryChange(index, event)}
                name="alternateGroupId"
              />
            </div>
          ))}
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <div className="flex space-x-4">
          <button
            type="submit"
            className="border border-[#ab997b] bg-black text-[#FBB033] font-be-jost-pro font-medium text-sm rounded-md px-10 py-2"
          >
            {isEdit ? "Save Changes" : "Create Combo"}
          </button>
          <button
            type="button"
            className="border border-[#ab997b] bg-black text-white font-be-jost-pro font-medium text-sm rounded-md px-10 py-2"
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceComboForm;
